// Client-side tenant resolver.
// Custom domain → slug mapping is now driven by the BunnyCDN-published
// _domains.json manifest (written by TripzOperator on every mini-site or
// domain change), so connecting a new custom domain no longer requires a
// TripzMini redeploy.

const SNAPSHOT_BASE =
  (import.meta as { env?: Record<string, string | undefined> }).env
    ?.VITE_SNAPSHOT_BASE || 'https://tripzsearch.b-cdn.net/triplinkConfigs/'

// Hardcoded fallback for custom domains. Mirror of api/_lib/tenant.ts.
//
// FROZEN (2026-06): do NOT add new entries. The _domains.json manifest is the
// source of truth — TripzOperator republishes it + purges the Bunny edge on
// every domain verify/activate/remove, and this resolver now fetches it with
// cache:'no-store', so newly-connected custom domains resolve with no TripzMini
// redeploy. These entries remain only as belt-and-suspenders for the original
// launch operators. Resolution order: hardcoded → manifest → 3-label subdomain
// → 'tripzsearch'.
const LEGACY_DOMAIN_MAP: Record<string, string> = {
  'relygo.in': 'relygo',
  'www.relygo.in': 'relygo',
  'tripzbooking.com': 'himachaladventuretours',
  'www.tripzbooking.com': 'himachaladventuretours',
  'canaratales.com': 'canaratales',
  'www.canaratales.com': 'canaratales',
  'bookntrek.com': 'bookntrek',
  'www.bookntrek.com': 'bookntrek',
  'himoraexpedition.com': 'himoraexpedition',
  'www.himoraexpedition.com': 'himoraexpedition',
  'vihantatoursandco.com': 'vihantatours',
  'www.vihantatoursandco.com': 'vihantatours',
  'flyhighflysafe.com': 'flyhighflysafe',
  'www.flyhighflysafe.com': 'flyhighflysafe',
}

type DomainsManifest = Record<string, string>

let manifestCache: { data: DomainsManifest; fetchedAt: number } | null = null
const MANIFEST_TTL_MS = 60_000

async function fetchDomainsManifest(): Promise<DomainsManifest> {
  const now = Date.now()
  if (manifestCache && now - manifestCache.fetchedAt < MANIFEST_TTL_MS) {
    return manifestCache.data
  }
  try {
    const res = await fetch(`${SNAPSHOT_BASE}_domains.json`, {
      // no-store (was force-cache): the manifest changes whenever an operator
      // connects a custom domain, but Bunny serves it with max-age=30d. Under
      // force-cache the browser pinned a stale copy for up to 30 days, so a
      // just-connected apex domain (e.g. flyhighflysafe.com) fell through to the
      // 'tripzsearch' default. The in-module manifestCache (60s) still keeps hot
      // requests off the network.
      cache: 'no-store',
    })
    if (!res.ok) return manifestCache?.data || {}
    const data = (await res.json()) as DomainsManifest
    manifestCache = { data, fetchedAt: now }
    return data
  } catch {
    return manifestCache?.data || {}
  }
}

function resolveFromHostSync(host: string): string | null {
  const hostNoPort = host.split(':')[0].toLowerCase()

  if (hostNoPort.endsWith('lvh.me')) {
    const sub = hostNoPort.replace('.lvh.me', '')
    return sub === 'lvh' ? 'tripzsearch' : sub
  }

  if (hostNoPort.endsWith('.tripz.link')) {
    return hostNoPort.replace('.tripz.link', '')
  }

  if (hostNoPort.endsWith('.tripz.live')) {
    return hostNoPort.replace('.tripz.live', '')
  }

  return null
}

/**
 * Resolve the tenant slug for the current host.
 *
 * Subdomain patterns resolve synchronously; custom domains fall back to the
 * async manifest fetch. Consumers should `await` this call.
 */
export async function resolveTenant(
  host: string,
  search: URLSearchParams,
): Promise<string> {
  const t = search.get('t')
  if (t) return t.toLowerCase()

  const hostNoPort = host.split(':')[0].toLowerCase()

  // Hardcoded fallback runs first so legacy + just-onboarded custom domains
  // resolve immediately even when the SPA's manifest fetch returns stale data.
  if (LEGACY_DOMAIN_MAP[hostNoPort]) return LEGACY_DOMAIN_MAP[hostNoPort]

  const sync = resolveFromHostSync(host)
  if (sync) return sync

  const manifest = await fetchDomainsManifest()
  if (manifest[hostNoPort]) return manifest[hostNoPort]

  // Generic fallback for any other 3-label host
  const parts = hostNoPort.split('.')
  if (parts.length >= 3) {
    return parts[0]
  }

  return 'tripzsearch'
}
